import React, { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useSnackbar } from "notistack";
import { find, uniq } from "lodash";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import { TextField, Typography } from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";

import axios from "../../../config/axios";
import { useDispatch, useSelector } from "react-redux";
import { getProductDetailsByProductType } from "utils";
import { getCustomer } from "redux/action/customer.action";
import { getOrder } from "redux/action/order.action";

const useStyles = makeStyles(theme => ({
  details: {
    display: "flex",
    flexDirection: "column",
    padding: "30px",
    width: "90%"
  },
  col1: {
    fontWeight: "500",
    minWidth: "110px",
    padding: "10PX"
  },
  row1: {
    display: "flex",
    margin: "11px 0px 11px 0px"
  }
}));

export default function ProductAddDialog({ orderId, allowOrderSelection, userId, priceVersion, setIsOpenAddProductDialog }) {
  const dispatch = useDispatch();
  const CUSTOMER_STATE = useSelector(state => state.customer);
  const ORDER_STATE = useSelector(state => state.order);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const subscriptionProducts = CUSTOMER_STATE?.customer?.subscription_products;
  const orderProducts = ORDER_STATE?.order?.products;
  const allPriceVersionProducts = ORDER_STATE?.allPriceVersionProducts;

  const orderIds = useMemo(() => uniq(subscriptionProducts?.map(product => product?.order_id?._id)), [subscriptionProducts]);
  const userProductTypes = useMemo(
    () => ORDER_STATE?.products && priceVersion && Object.keys(find(ORDER_STATE.products, product => product.price_version === priceVersion))?.slice(1),
    [ORDER_STATE, priceVersion]
  );

  const [selectedOrderId, setSelectedOrderId] = useState(orderId || orderIds?.[0]);

  const existingProducts = useMemo(() => {
    if (orderId) return orderProducts ?? [];
    return subscriptionProducts?.filter(
      p => (p.order_id?._id ?? p.order_id) === selectedOrderId
    ) ?? [];
  }, [orderId, selectedOrderId, orderProducts, subscriptionProducts]);

  const hasBox = existingProducts.some(p => p.product_type_id?.type === "box");
  const hasSpace = existingProducts.some(p => {
    const t = p.product_type_id?.type;
    return t && t !== "box";
  });

  const isProductTypeDisabled = useCallback(
    productType => {
      const details = getProductDetailsByProductType(allPriceVersionProducts?.[priceVersion], productType);
      if (!details) return false;
      if (details.type === "box") return hasBox;
      return hasSpace;
    },
    [allPriceVersionProducts, priceVersion, hasBox, hasSpace]
  );

  const availableProductTypes = useMemo(
    () => userProductTypes?.filter(pt => !isProductTypeDisabled(pt)) ?? [],
    [userProductTypes, isProductTypeDisabled]
  );

  const [newProductType, setNewProductType] = useState(userProductTypes?.[0]);

  React.useEffect(() => {
    if (isProductTypeDisabled(newProductType)) {
      setNewProductType(availableProductTypes[0] ?? null);
    }
  }, [selectedOrderId, hasBox, hasSpace, availableProductTypes, isProductTypeDisabled, newProductType]);

  const handleProductTypeChange = useCallback(event => {
    setNewProductType(event.target.value);
  }, []);

  const handleOrderIdChange = useCallback(event => {
    setSelectedOrderId(event.target.value);
  }, []);

  const handleCloseAddProductDialog = useCallback(() => {
    setIsOpenAddProductDialog(false);
  }, [setIsOpenAddProductDialog]);

  const handleAddProduct = useCallback(async () => {
    if (isProductTypeDisabled(newProductType)) {
      enqueueSnackbar(t("addProduct.productAlreadyExists"), { variant: "error" });
      return;
    }
    const body = {
      user_id: userId,
      product_type: newProductType,
      order_id: selectedOrderId
    };
    setLoading(true);
    await axios
      .patch(`/admin/upgrade-subscription/add-product`, body)
      .then(res => {
        setIsOpenAddProductDialog(false);
        if (orderId) {
          dispatch(getOrder({ id: orderId, type: "purchase-order" }));
        } else {
          dispatch(getCustomer({ id: CUSTOMER_STATE?.customer?._id }));
        }
        enqueueSnackbar(t("messages.productAdded"), { variant: "success" });
      })
      .catch(error => {
        enqueueSnackbar(error.response.data.message, { variant: "error" });
        setLoading(false);
        return error;
      });
  }, [dispatch, enqueueSnackbar, setIsOpenAddProductDialog, CUSTOMER_STATE, newProductType, selectedOrderId, orderId, userId, t, isProductTypeDisabled]);

  const classes = useStyles();

  return (
    <React.Fragment>
      <Dialog fullWidth={true} maxWidth={"sm"} open={true} onClose={handleCloseAddProductDialog} aria-labelledby="max-width-dialog-title">
        <div className={classes.details}>
          <div className={classes.row1}>
            <div className={classes.col1}>{t("addProduct.productName")}</div>
            <div style={{ width: "400px" }}>
              {availableProductTypes?.length > 0 ? (
                <TextField id="standard-select-status" select label={t("addProduct.product")} value={newProductType} fullWidth={true} onChange={e => handleProductTypeChange(e)} variant="outlined">
                  {availableProductTypes?.map(productType => (
                    <MenuItem key={productType} value={productType}>
                      {getProductDetailsByProductType(allPriceVersionProducts?.[priceVersion], productType).name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <Typography color="textSecondary" variant="body2">
                  {t("addProduct.noProductsAvailable")}
                </Typography>
              )}
            </div>
          </div>
          {allowOrderSelection && (
            <div className={classes.row1}>
              <div className={classes.col1}>{t("addProduct.orderId")}</div>
              <div style={{ width: "400px" }}>
                <TextField id="standard-select-status" select label={t("addProduct.orderId")} value={selectedOrderId} fullWidth={true} onChange={e => handleOrderIdChange(e)} variant="outlined">
                  {orderIds?.map(orderId => (
                    <MenuItem key={orderId} value={orderId}>
                      {orderId}
                    </MenuItem>
                  ))}
                </TextField>
              </div>
            </div>
          )}
        </div>
        <DialogActions>
          <Button onClick={handleAddProduct} color="primary" disabled={loading || availableProductTypes.length === 0}>
            {loading ? t("addProduct.loading") : t("addProduct.addProduct")}
          </Button>

          <Button onClick={handleCloseAddProductDialog} color="primary">
            {t("addProduct.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
